import type { Json } from '@fundline/db/supabase';
import {
  type CampaignSettingsForm,
  readCampaignSettings,
  toCampaignSettingsForm,
  toStoredCampaignSettings,
} from '@fundline/shared/campaigns/settings';
import {
  type Audience,
  type CampaignDraft,
  CampaignPreviewSchema,
  CampaignStatsSchema,
  CampaignStatusResponseSchema,
  CreateCampaignResponseSchema,
  PauseAllResponseSchema,
  readAudience,
  TestSendResponseSchema,
} from '@fundline/shared/schemas/campaigns';
import { OkResponseSchema } from '@fundline/shared/schemas/imports';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys.ts';
import { supabase } from '../../lib/supabase.ts';
import { workerApi } from '../../lib/worker-api.ts';

/**
 * Campaigns (M9). Admins only: lists and details come from Supabase (RLS: admins read), every action
 * goes through the worker, which re-checks the role and enforces every rule. While a campaign runs, its
 * screen refetches every few seconds (it changes as the pacer works through it).
 */

export type CampaignStatus =
  'draft' | 'scheduled' | 'running' | 'paused' | 'completed' | 'cancelled';

export interface CampaignSummary {
  id: string;
  name: string;
  status: CampaignStatus;
  audience: Audience;
  template: string;
  maxSegments: number;
  frequencyDays: number;
  aiEnabled: boolean;
  aiModel: 'haiku' | 'sonnet';
  scheduledAt: string | null;
  startedAt: string | null;
  expiresAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  pausedAt: string | null;
  pauseReason: string | null;
  recipientCount: number;
  eligibleCount: number;
  skippedCount: number;
  estimateCents: string | null;
  footer: string | null;
  createdAt: string;
  createdBy: string;
}

const COLUMNS =
  'id, name, status, audience, message_template, max_segments, frequency_days, ai_enabled, ai_model, scheduled_at, started_at, expires_at, completed_at, cancelled_at, paused_at, pause_reason, recipient_count, eligible_count, skipped_count, estimate_cents, footer, created_at, created_by';

type Row = {
  id: string;
  name: string;
  status: CampaignStatus;
  audience: Json;
  message_template: string;
  max_segments: number;
  frequency_days: number;
  ai_enabled: boolean;
  ai_model: 'haiku' | 'sonnet';
  scheduled_at: string | null;
  started_at: string | null;
  expires_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  paused_at: string | null;
  pause_reason: string | null;
  recipient_count: number;
  eligible_count: number;
  skipped_count: number;
  estimate_cents: number | string | null;
  footer: string | null;
  created_at: string;
  created_by: string;
};

function toSummary(r: Row): CampaignSummary {
  return {
    id: r.id,
    name: r.name,
    status: r.status,
    audience: readAudience(r.audience),
    template: r.message_template,
    maxSegments: r.max_segments,
    frequencyDays: r.frequency_days,
    aiEnabled: r.ai_enabled,
    aiModel: r.ai_model,
    scheduledAt: r.scheduled_at,
    startedAt: r.started_at,
    expiresAt: r.expires_at,
    completedAt: r.completed_at,
    cancelledAt: r.cancelled_at,
    pausedAt: r.paused_at,
    pauseReason: r.pause_reason,
    recipientCount: r.recipient_count,
    eligibleCount: r.eligible_count,
    skippedCount: r.skipped_count,
    estimateCents: r.estimate_cents === null ? null : String(r.estimate_cents),
    footer: r.footer,
    createdAt: r.created_at,
    createdBy: r.created_by,
  };
}

const LIVE = new Set<CampaignStatus>(['scheduled', 'running', 'paused']);

export function useCampaignsQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.campaigns(workspaceId),
    queryFn: async (): Promise<CampaignSummary[]> => {
      const { data, error } = await supabase()
        .from('campaigns')
        .select(COLUMNS)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw new Error(`Couldn't load campaigns: ${error.message}`);
      return (data as Row[]).map(toSummary);
    },
    refetchInterval: (q) => (q.state.data?.some((c) => LIVE.has(c.status)) ? 10_000 : false),
  });
}

export function useCampaignQuery(workspaceId: string, campaignId: string) {
  return useQuery({
    queryKey: queryKeys.campaign(workspaceId, campaignId),
    queryFn: async (): Promise<CampaignSummary> => {
      const { data, error } = await supabase()
        .from('campaigns')
        .select(COLUMNS)
        .eq('workspace_id', workspaceId)
        .eq('id', campaignId)
        .single();
      if (error) throw new Error(`Couldn't load the campaign: ${error.message}`);
      return toSummary(data);
    },
    refetchInterval: (q) => (q.state.data && LIVE.has(q.state.data.status) ? 5_000 : false),
  });
}

/** What the campaign would do now (worker): who gets it, who is skipped, cost, timing, problems. */
export function useCampaignPreviewQuery(workspaceId: string, campaignId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.campaignPreview(workspaceId, campaignId),
    queryFn: () =>
      workerApi(`/workspaces/${workspaceId}/campaigns/${campaignId}/preview`, {
        schema: CampaignPreviewSchema,
      }),
    enabled,
    staleTime: 0,
  });
}

export function useCampaignStatsQuery(workspaceId: string, campaignId: string, live: boolean) {
  return useQuery({
    queryKey: queryKeys.campaignStats(workspaceId, campaignId),
    queryFn: () =>
      workerApi(`/workspaces/${workspaceId}/campaigns/${campaignId}/stats`, {
        schema: CampaignStatsSchema,
      }),
    refetchInterval: live ? 5_000 : false,
  });
}

export interface RecipientRow {
  merchantId: string;
  position: number;
  status: 'pending' | 'queued' | 'skipped';
  skipReason: string | null;
  label: string;
  messageStatus: string | null;
  messageReason: string | null;
}

export const RECIPIENT_PAGE = 50;

/** One page of recipients, in audience order; optionally only skipped (by reason) or queued ones. */
export function useRecipientsQuery(
  workspaceId: string,
  campaignId: string,
  filter: { status: 'all' | 'queued' | 'skipped'; page: number },
  live: boolean,
) {
  return useQuery({
    queryKey: queryKeys.campaignRecipients(workspaceId, campaignId, filter),
    queryFn: async (): Promise<{ rows: RecipientRow[]; total: number }> => {
      let q = supabase()
        .from('campaign_recipients')
        .select(
          'merchant_id, position, status, skip_reason, merchants(business_name, owner_name, phone), messages(status, status_reason)',
          { count: 'exact' },
        )
        .eq('workspace_id', workspaceId)
        .eq('campaign_id', campaignId);
      if (filter.status !== 'all') q = q.eq('status', filter.status);
      const from = filter.page * RECIPIENT_PAGE;
      const { data, error, count } = await q
        .order('position')
        .range(from, from + RECIPIENT_PAGE - 1);
      if (error) throw new Error(`Couldn't load the recipients: ${error.message}`);
      type R = {
        merchant_id: string;
        position: number;
        status: RecipientRow['status'];
        skip_reason: string | null;
        merchants: {
          business_name: string | null;
          owner_name: string | null;
          phone: string;
        } | null;
        messages: { status: string; status_reason: string | null } | null;
      };
      return {
        total: count ?? 0,
        rows: (data as unknown as R[]).map((r) => ({
          merchantId: r.merchant_id,
          position: r.position,
          status: r.status,
          skipReason: r.skip_reason,
          label:
            r.merchants?.business_name?.trim() ||
            r.merchants?.owner_name?.trim() ||
            r.merchants?.phone ||
            'Merchant',
          messageStatus: r.messages?.status ?? null,
          messageReason: r.messages?.status_reason ?? null,
        })),
      };
    },
    refetchInterval: live ? 10_000 : false,
  });
}

/** Names of campaigns for thread labels (every member; their own workspace only). */
export function useCampaignLabels(workspaceId: string, ids: readonly string[]) {
  const sorted = [...new Set(ids)].sort();
  return useQuery({
    queryKey: queryKeys.campaignLabels(workspaceId, sorted),
    queryFn: async (): Promise<Map<string, string>> => {
      const { data, error } = await supabase().rpc('campaign_labels', { p_ids: sorted });
      if (error) throw new Error(`Couldn't load campaign names: ${error.message}`);
      return new Map((data ?? []).map((r) => [r.id, r.name]));
    },
    enabled: sorted.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useCampaignActions(workspaceId: string, campaignId: string) {
  const queryClient = useQueryClient();
  const base = `/workspaces/${workspaceId}/campaigns/${campaignId}`;
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.campaigns(workspaceId) });
  const status = (action: string, body: unknown = {}) =>
    workerApi(`${base}/${action}`, { method: 'POST', body, schema: CampaignStatusResponseSchema });
  return {
    save: useMutation({
      mutationFn: (draft: CampaignDraft) =>
        workerApi(base, { method: 'POST', body: draft, schema: OkResponseSchema }),
      onSuccess: refresh,
    }),
    launch: useMutation({
      mutationFn: (input: { launchKey: string; confirmMultiSegment: boolean }) =>
        status('launch', input),
      onSettled: refresh,
    }),
    schedule: useMutation({
      mutationFn: (input: { at: string; confirmMultiSegment: boolean }) =>
        status('schedule', input),
      onSettled: refresh,
    }),
    unschedule: useMutation({ mutationFn: () => status('unschedule'), onSettled: refresh }),
    pause: useMutation({ mutationFn: () => status('pause'), onSettled: refresh }),
    resume: useMutation({ mutationFn: () => status('resume'), onSettled: refresh }),
    cancel: useMutation({ mutationFn: () => status('cancel'), onSettled: refresh }),
    duplicate: useMutation({
      mutationFn: () =>
        workerApi(`${base}/duplicate`, {
          method: 'POST',
          body: {},
          schema: CreateCampaignResponseSchema,
        }),
      onSuccess: refresh,
    }),
    test: useMutation({
      mutationFn: (input: { merchantId: string; clientKey: string }) =>
        workerApi(`${base}/test`, { method: 'POST', body: input, schema: TestSendResponseSchema }),
    }),
  };
}

export function useCreateCampaign(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: CampaignDraft) =>
      workerApi(`/workspaces/${workspaceId}/campaigns`, {
        method: 'POST',
        body: draft,
        schema: CreateCampaignResponseSchema,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.campaigns(workspaceId) }),
  });
}

export function usePauseAll(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      workerApi(`/workspaces/${workspaceId}/campaigns/pause-all`, {
        method: 'POST',
        body: {},
        schema: PauseAllResponseSchema,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.campaigns(workspaceId) }),
  });
}

/** Lead sources (merchants.source values) for the audience builder. */
export function useMerchantSourcesQuery(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.merchantSources(workspaceId),
    queryFn: async (): Promise<string[]> => {
      const [sources, imports] = await Promise.all([
        supabase()
          .from('intake_sources')
          .select('name')
          .eq('workspace_id', workspaceId)
          .is('archived_at', null),
        supabase().from('merchants').select('source').eq('workspace_id', workspaceId).limit(1000),
      ]);
      if (sources.error) throw new Error(sources.error.message);
      if (imports.error) throw new Error(imports.error.message);
      return [
        ...new Set([...sources.data.map((s) => s.name), ...imports.data.map((m) => m.source)]),
      ].sort((a, b) => a.localeCompare(b));
    },
  });
}

// ─────────────────────────── Settings → Campaigns ───────────────────────────

type JsonObject = { [key: string]: Json | undefined };

function asObject(value: Json): JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export interface CampaignSettingsData {
  workspaceName: string;
  form: CampaignSettingsForm;
  consentSources: string[];
  rawSettings: JsonObject;
}

const settingsKey = (workspaceId: string) =>
  [...queryKeys.workspace(workspaceId), 'campaigns'] as const;

export function useCampaignSettingsQuery(workspaceId: string) {
  return useQuery({
    queryKey: settingsKey(workspaceId),
    queryFn: async (): Promise<CampaignSettingsData> => {
      const { data, error } = await supabase()
        .from('workspaces')
        .select('name, settings, campaign_consent_sources')
        .eq('id', workspaceId)
        .single();
      if (error) throw new Error(`Couldn't load the settings: ${error.message}`);
      return {
        workspaceName: data.name,
        form: toCampaignSettingsForm(readCampaignSettings(data.settings)),
        consentSources: data.campaign_consent_sources,
        rawSettings: asObject(data.settings),
      };
    },
  });
}

/** Saves the campaigns section only (admins: column grant on settings; the database checks it too). */
export function useSaveCampaignSettings(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { form: CampaignSettingsForm; rawSettings: JsonObject }) => {
      const settings: JsonObject = {
        ...input.rawSettings,
        campaigns: toStoredCampaignSettings(input.form),
      };
      const { error } = await supabase()
        .from('workspaces')
        .update({ settings })
        .eq('id', workspaceId);
      if (error) throw new Error(`Couldn't save: ${error.message}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.workspace(workspaceId) }),
  });
}
